'use client';

import {
  CubeIcon,
  BuildingsIcon,
  CaretUpDownIcon,
  ClipboardTextIcon,
  FileTextIcon,
  FlagIcon,
  FlaskIcon,
  GearIcon,
  LinkIcon,
  ListChecksIcon,
  SignOutIcon,
  UserIcon,
  FlowArrowIcon,
  HeadCircuitIcon,
  WrenchIcon,
  ClockIcon,
  UserSwitchIcon,
} from '@phosphor-icons/react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { T } from 'gt-react';
import { AuthContext } from '@/context/auth.context';
import { useContext, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import paths from '@/paths';
import CurrentUser from 'src/types/CurrentUser';
import { isAdmin } from '@/utils/user';
import { useViewMode } from '@/context/view-mode.context';
import { useCan } from '@/hooks/useCan';
import { Action, Subject } from '@erp/authz';
import { useFeatureFlag } from 'src/integrations/feature-flags/feature-flags';
import config from 'src/config';
import { CopyrightNotice } from 'src/components/common/CopyrightNotice';
import { ImitateUserDialog } from './ImitateUserDialog';
import { FeedbackMenuItem } from './FeedbackMenuItem';
import { InboundEmailsMenuItem } from './InboundEmailsMenuItem';
import { DEFAULT_ORG_SECTION_ID } from 'src/pages/authenticated/settings/organization/organizationSettingsSections';
import { useVisibleOrganizationSettingsSections } from 'src/pages/authenticated/settings/organization/useVisibleOrganizationSettingsSections';

function LabelledUserAvatar({
  currentUser,
}: {
  currentUser: CurrentUser | undefined;
}) {
  return (
    <>
      <Avatar className="h-8 w-8 rounded-lg">
        <AvatarImage src={undefined} alt={currentUser?.name} />
        <AvatarFallback className="rounded-lg">
          <UserIcon />
        </AvatarFallback>
      </Avatar>
      <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
        <span className="truncate font-medium">{currentUser?.name}</span>
        <span className="truncate text-xs">{currentUser?.emails[0]}</span>
      </div>
    </>
  );
}

/** The copyright notice, tucked at the foot of the user menu. */
function MenuCopyrightNotice() {
  return (
    <div className="select-none px-2 pb-0.5 text-center leading-none">
      <CopyrightNotice className="text-[10px]" />
    </div>
  );
}

export function NavUser() {
  const { isMobile } = useSidebar();
  const { currentUser, logOut } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const navigate = useNavigate();
  const [canReadIntegrations] = useCan(Action.Read, Subject.Integration);
  // Manage Organization opens for anyone who may open at least one settings
  // section (each section carries its own RBAC gate), landing on General for
  // Gently staff and on the first section the viewer may see otherwise.
  const visibleSettingsSections = useVisibleOrganizationSettingsSections();
  const canManageOrganization = visibleSettingsSections.length > 0;
  const manageOrganizationSection = visibleSettingsSections.some(
    (section) => section.id === 'general',
  )
    ? 'general'
    : (visibleSettingsSections[0]?.id ?? DEFAULT_ORG_SECTION_ID);
  const [canReadBackgroundAgents] = useCan(
    Action.Read,
    Subject.BackgroundAgent,
  );
  const [canReadTasks] = useCan(Action.Read, Subject.Task);
  const { enabled: tasksEnabled } = useFeatureFlag('tasks-enabled', false);
  const [canReportSalesOrder] = useCan(Action.Report, Subject.SalesOrder);
  const [canReportSalesOrderDraft] = useCan(
    Action.Report,
    Subject.SalesOrderDraft,
  );
  const [canReportPurchaseOrder] = useCan(Action.Report, Subject.PurchaseOrder);
  const canReportAny =
    canReportSalesOrder || canReportSalesOrderDraft || canReportPurchaseOrder;
  const [openingControlPlane, setOpeningControlPlane] = useState(false);
  const [imitateDialogOpen, setImitateDialogOpen] = useState(false);

  const isRestrictedUser = currentUser?.isRestrictedUser ?? false;
  // Imitation can't be nested, and while imitating `currentUser` describes the
  // target rather than the admin driving the session — so the entry point only
  // exists for a real Gently admin who isn't imitating anyone yet.
  const canImitate =
    !isRestrictedUser &&
    isAdmin(currentUser, viewMode) &&
    !currentUser?.imitation;

  const handleLogout = async () => {
    try {
      await logOut();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleOpenControlPlane = async () => {
    if (openingControlPlane) {
      return;
    }

    setOpeningControlPlane(true);
    let response: Response;

    try {
      response = await fetch(`${config.apiBaseUrl}/auth/control-plane`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Control plane handoff failed:', error);
      setOpeningControlPlane(false);
      return;
    }

    const payload = (await response.json().catch(() => null)) as {
      url?: string;
      message?: string;
    } | null;

    if (!response.ok || !payload?.url) {
      console.error(
        'Control plane handoff failed:',
        payload?.message ?? 'Unable to open control plane',
      );
      setOpeningControlPlane(false);
      return;
    }

    window.location.assign(payload.url);
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[popup-open]:bg-sidebar-accent data-[popup-open]:text-sidebar-accent-foreground"
            >
              <LabelledUserAvatar currentUser={currentUser} />
              <CaretUpDownIcon className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--anchor-width) min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <LabelledUserAvatar currentUser={currentUser} />
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() =>
                  navigate({ to: paths.authenticated.settings.user })
                }
              >
                <GearIcon />
                <T>User Preferences</T>
              </DropdownMenuItem>
              {!isRestrictedUser && canManageOrganization && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({
                      to: paths.authenticated.settings.organization,
                      search: { section: manageOrganizationSection },
                    })
                  }
                >
                  <BuildingsIcon />
                  <T>Manage Organization</T>
                </DropdownMenuItem>
              )}
              {!isRestrictedUser && canReadTasks && tasksEnabled && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({
                      to: paths.authenticated.settings.organization,
                      search: { section: 'task-types' },
                    })
                  }
                >
                  <ListChecksIcon />
                  <T context="nav user menu link to organization task types settings">
                    Task Types
                  </T>
                </DropdownMenuItem>
              )}
              {!isRestrictedUser && currentUser?.tenantUser?.isAdmin && (
                <DropdownMenuItem
                  onClick={() => navigate({ to: paths.authenticated.tenant })}
                >
                  <BuildingsIcon />
                  <T>Manage Tenant</T>
                </DropdownMenuItem>
              )}
              {!isRestrictedUser && isAdmin(currentUser, viewMode) && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({ to: paths.authenticated.documents })
                  }
                >
                  <FileTextIcon />
                  <T>Documents</T>
                </DropdownMenuItem>
              )}
              <InboundEmailsMenuItem
                admin={isAdmin(currentUser, viewMode)}
                restricted={isRestrictedUser}
              />
              <DropdownMenuItem
                onClick={() =>
                  navigate({ to: paths.authenticated.sharedLinks })
                }
              >
                <LinkIcon />
                <T>Shared Links</T>
              </DropdownMenuItem>
              {!isRestrictedUser && canReadIntegrations && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({ to: paths.authenticated.integrations.list })
                  }
                >
                  <CubeIcon />
                  <T>Integrations</T>
                </DropdownMenuItem>
              )}
              {!isRestrictedUser && canReportAny && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({ to: paths.authenticated.auditReports })
                  }
                >
                  <ClipboardTextIcon />
                  <T>Audit Reports</T>
                </DropdownMenuItem>
              )}
              <FeedbackMenuItem />
              {!isRestrictedUser && isAdmin(currentUser, viewMode) && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({ to: paths.authenticated.workflows.list })
                  }
                >
                  <FlowArrowIcon />
                  <T>Workflows</T>
                </DropdownMenuItem>
              )}
              {!isRestrictedUser && isAdmin(currentUser, viewMode) && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({
                      to: paths.authenticated.workflows.scheduledReruns,
                    })
                  }
                >
                  <ClockIcon />
                  <T>Scheduled Reruns</T>
                </DropdownMenuItem>
              )}
              {!isRestrictedUser && isAdmin(currentUser, viewMode) && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({ to: paths.authenticated.maintenance.root })
                  }
                >
                  <WrenchIcon />
                  <T context="user menu item: admin backfills and index builds">
                    Maintenance
                  </T>
                </DropdownMenuItem>
              )}
              {!isRestrictedUser &&
                isAdmin(currentUser, viewMode) &&
                canReadBackgroundAgents && (
                  <DropdownMenuItem
                    onClick={() =>
                      navigate({
                        to: paths.authenticated.backgroundAgents.list,
                      })
                    }
                  >
                    <HeadCircuitIcon />
                    <T>Background Agents</T>
                  </DropdownMenuItem>
                )}
              {!isRestrictedUser && isAdmin(currentUser, viewMode) && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({ to: paths.authenticated.evals.root })
                  }
                >
                  <FlaskIcon />
                  <T>Evals</T>
                </DropdownMenuItem>
              )}
              {!isRestrictedUser && isAdmin(currentUser, viewMode) && (
                <DropdownMenuItem
                  disabled={openingControlPlane}
                  onClick={handleOpenControlPlane}
                >
                  <CubeIcon />
                  {openingControlPlane ? (
                    <T>Opening Control Plane...</T>
                  ) : (
                    <T>Control Plane</T>
                  )}
                </DropdownMenuItem>
              )}
              {!isRestrictedUser && isAdmin(currentUser, viewMode) && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({ to: paths.authenticated.featureFlags })
                  }
                >
                  <FlagIcon />
                  <T>Feature Flags</T>
                </DropdownMenuItem>
              )}
              {canImitate && (
                <DropdownMenuItem onClick={() => setImitateDialogOpen(true)}>
                  <UserSwitchIcon />
                  <T>Imitate a user…</T>
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <SignOutIcon />
              <T>Log out</T>
            </DropdownMenuItem>
            <MenuCopyrightNotice />
          </DropdownMenuContent>
        </DropdownMenu>
        {/* Outside the DropdownMenu: selecting an item closes the menu, which
            would unmount a dialog nested inside it before it ever painted. */}
        {canImitate && (
          <ImitateUserDialog
            open={imitateDialogOpen}
            onOpenChange={setImitateDialogOpen}
          />
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
